import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SmartOnboardingWizard from "../components/SmartOnboardingWizard";

export default function OrganizationSetupPage() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrganizationData();
  }, []);

  const fetchOrganizationData = async () => {
    try {
      const orgId = user?.org_id;
      const orgRes = await api.get("/bcm-admin/organization");
      const deptRes = await api.get("/bcm-admin/departments").catch(() => ({ data: [] }));
      const locRes = await api.get("/bcm-admin/locations").catch(() => ({ data: [] }));
      // Existing endpoints that expose the saved enrichment/classification/identifier
      // values so the re-opened wizard pre-fills every saved field (form load only).
      const enrRes = orgId ? await api.get(`/enrichment/org/${orgId}`).catch(() => ({ data: {} })) : { data: {} };
      const curRes = await api.get("/organizations/current").catch(() => ({ data: {} }));

      const ed = enrRes.data?.enrichment_data || {};
      const cd = enrRes.data?.classification_data || {};
      const ci = enrRes.data?.compliance_identifiers || orgRes.data?.compliance_identifiers || {};
      const im = curRes.data?.industry_metadata || orgRes.data?.industry_metadata || {};

      setInitialData({
        name: orgRes.data?.name || ed.legal_name || "",
        industry: orgRes.data?.industry || "general",
        sector: im?.sector || "",
        sub_industry: im?.sub_industry || orgRes.data?.industry || "",
        sub_industries: im?.sub_industries || (im?.sub_industry ? [im.sub_industry] : []),
        phone: orgRes.data?.phone || "",
        website: orgRes.data?.website || ed.domain || "",
        entity_type: ed.entity_type || ed.public_private || "",
        duns_number: ci.duns_number || "",
        year_founded: ed.year_founded ? String(ed.year_founded) : "",
        company_summary: ed.company_summary || "",
        parent_company: ed.parent || ed.immediate_parent || "",
        naics_primary: cd.naics_primary ? String(cd.naics_primary) : "",
        sic_primary: cd.sic_primary ? String(cd.sic_primary) : "",
        ein: ci.ein || "",
        employee_count: ed.employee_count != null ? String(ed.employee_count) : "",
        tech_stack: ed.tech_stack || [],
        annual_revenue: ed.annual_revenue != null ? String(ed.annual_revenue) : "",
        revenue_fiscal_year: ed.revenue_fiscal_year != null ? String(ed.revenue_fiscal_year) : "",
        certifications: orgRes.data?.certifications || [],
        products_services: ed.products_services || [],
        subsidiaries: [...(ed.subsidiary_names || []), ...(ed.detected_subsidiaries || [])],
        locations: (locRes.data || []).filter((l) => l.status !== "retired").length > 0 ? locRes.data.filter((l) => l.status !== "retired") : undefined,
        departments: deptRes.data?.length > 0 ? deptRes.data : undefined,
      });
    } catch (error) {
      console.error("Failed to fetch organization data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async (data) => {
    try {
      // Update organization details
      await api.put("/organizations/current", {
        name: data.organization.name,
        industry: data.organization.industry,
        phone: data.organization.phone,
        address: data.locations[0]?.address || "",
        industry_metadata: data.organization.industry_metadata,
        ...(data.manual_codes || {}),
      });

      // Save locations (S2: every failure is collected and shown)
      const failed = [];
      const why = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : (d?.message || e?.message || "error"); };
      for (const location of data.locations) {
        await api.post("/bcm-admin/locations", {
          name: location.name,
          address: location.address,
          latitude: location.latitude,
          longitude: location.longitude,
          type: location.type,
          function: location.function,
          phone: location.phone,
          street: location.street,
          city: location.city,
          state: location.state,
          postal_code: location.postal_code,
          country: location.country,
          risk_analysis: data.location_risks[location.id] || null
        }).catch(e => failed.push(`Site ${location.name}: ${why(e)}`));
      }

      // Save departments IN PLACE (S1): match each form department to an existing
      // one by id, else by exact name; update matches, create only new ones.
      // Departments not in the form are left unchanged (delete is in Org Management).
      let existingDepts = [];
      try {
        existingDepts = (await api.get("/bcm-admin/departments")).data || [];
      } catch (e) { console.log("Load existing departments:", e); }
      let savedLocIds = new Set();
      try {
        savedLocIds = new Set(((await api.get("/bcm-admin/locations")).data || []).map((l) => l.id));
      } catch (e) { console.log("Load saved sites:", e); }

      for (const dept of data.departments) {
        const match = existingDepts.find((ex) => dept.id && ex.id === dept.id)
          || existingDepts.find((ex) => ex.name === dept.name);
        const locIds = dept.location_ids || [];
        if (match) {
          const body = {
            name: dept.name,
            description: dept.description,
            criticality: dept.criticality,
            service_lines: dept.service_lines || [],
          };
          if (locIds.length && locIds.every((id) => savedLocIds.has(id))) body.location_ids = locIds;
          await api.put(`/bcm-admin/departments/${match.id}`, body).catch(e => failed.push(`Department ${dept.name}: ${why(e)}`));
          continue;
        }
        await api.post("/bcm-admin/departments", {
          name: dept.name,
          description: dept.description,
          criticality: dept.criticality,
          service_lines: dept.service_lines || [],
          suggested_rto: dept.suggested_rto || null,
          suggested_mtpd: dept.suggested_mtpd || null,
          location_ids: locIds,
        }).catch(e => failed.push(`Department ${dept.name}: ${why(e)}`));
      }

      if (failed.length) {
        toast.error(`Some items were not saved: ${failed.join("; ")}`, { duration: 20000 });
        return;
      }
      toast.success("Organization setup completed!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Setup failed:", error);
      toast.error("Failed to save organization data");
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-8 px-4" data-testid="organization-setup-page">
      <p className="mb-4 rounded border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800" data-testid="dept-save-in-place-note">
        Saving updates your departments in place; to delete a department use Organization Management.
      </p>
      <SmartOnboardingWizard 
        initialData={initialData}
        onComplete={handleComplete}
      />
    </div>
  );
}
